const TRACKING_PARAMETER_PREFIX_PATTERN = /^(?:utm_|mc_|pk_)/u

const TRACKING_PARAMETER_NAMES = new Set<string>([
  'fbclid',
  'gclid',
  'gclsrc',
  'dclid',
  'msclkid',
  'igshid',
  'ref',
  'source',
])

/**
 * Normalises a listing URL into a canonical key for equality comparison
 * across the SDK save flow. Two URLs that point at the same listing but
 * differ only by protocol, a leading `www.`, a trailing slash, the fragment,
 * or common tracking query parameters (`utm_*`, `fbclid`, `gclid`, …) collapse
 * to the same string. Remaining query parameters are lower-cased and sorted so
 * ordering never affects the result. Never throws — an unparseable input is
 * lower-cased and trimmed so callers can still compare it directly.
 *
 * @param {string} url - the raw listing URL
 * @returns {string} the canonical comparison key (empty string for empty input)
 */
const normalizeListingUrl = (url: string): string => {
  const trimmed = typeof url === 'string' ? url.trim() : ''

  if (trimmed === '') {
    return ''
  }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return trimmed.toLowerCase()
  }

  const hostname = parsed.hostname.toLowerCase().replace(/^www\./u, '')

  let pathname = parsed.pathname
  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.replace(/\/+$/u, '')
  }

  const keptParameters: Array<[string, string]> = []
  parsed.searchParams.forEach((value, key) => {
    const lowerCaseKey = key.toLowerCase()

    if (TRACKING_PARAMETER_PREFIX_PATTERN.test(lowerCaseKey) === true) {
      return
    }

    if (TRACKING_PARAMETER_NAMES.has(lowerCaseKey) === true) {
      return
    }

    keptParameters.push([lowerCaseKey, value])
  })

  keptParameters.sort((first, second) => {
    if (first[0] !== second[0]) {
      return first[0] < second[0] ? -1 : 1
    }

    if (first[1] === second[1]) {
      return 0
    }

    return first[1] < second[1] ? -1 : 1
  })

  const query = keptParameters
    .map(parameter => `${parameter[0]}=${parameter[1]}`)
    .join('&')

  const base = `${hostname}${pathname}`

  return query === '' ? base : `${base}?${query}`
}

export { normalizeListingUrl }
